<template>
  <div
    v-click-tracker="analyticsParams"
    v-view-tracker="analyticsParams"
    class="card"
    @click="sightStore.useSight(sight)"
  >
    <Img
      :src="sight.cover_photo?.thumbnail"
      width="120px"
      height="160px"
      :alt="sight.name"
      border-radius="8px"
    />
    <div class="text">
      <div
        ref="title"
        class="title"
      >
        {{ sight.name }}
      </div>
      <div
        ref="subtitle"
        :class="['subtitle', { hidden: viewMore }]"
      >
        {{ sight.description }}
        <div
          v-if="viewMore"
          class="more"
        >
          Ещё
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { nextTick, ref, useTemplateRef } from 'vue';

  import { useClickTracker, useViewTracker } from '@/services/analytics';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import { useSightsStore } from '@/shared/store/sights';
  import Img from '@/shared/ui/ResponsiveImage.vue';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';

  const props = defineProps<{
    sight: CityTagsAdvanced;
  }>();

  // @ts-expect-error события нет в схеме Sogu
  const vViewTracker = useViewTracker('sights:sights-snippet.view');
  // @ts-expect-error события нет в схеме Sogu
  const vClickTracker = useClickTracker('sights:sights-snippet.click');

  const titleRef = useTemplateRef('title');
  const subtitleRef = useTemplateRef('subtitle');

  const cardHeight = 160;
  const lineHeight = 20;

  const sightStore = useSightsStore();

  const maxSubtitleHeight = ref();
  const maxSubtitleColumn = ref();
  const subtitleColumns = ref();
  const viewMore = ref();

  void nextTick(() => {
    maxSubtitleHeight.value = cardHeight - (titleRef.value?.offsetHeight ?? 0);
    maxSubtitleColumn.value = maxSubtitleHeight.value / lineHeight;
    subtitleColumns.value = (subtitleRef.value?.scrollHeight ?? 0) / lineHeight;
    viewMore.value = subtitleColumns.value > maxSubtitleColumn.value;
  });

  const analyticsParams = useSightAnalyticsParams(() => props.sight);
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;

  .card {
    display: flex;
    gap: 8px;
    width: 100%;
    color: var(--alias-color-text-default);
    text-decoration: none;
    cursor: pointer;
  }

  .title {
    @include legacy-typography.base-400-300-semi;
  }

  .subtitle {
    position: relative;
    display: -webkit-box;
    overflow: auto;
    -webkit-line-clamp: v-bind('maxSubtitleColumn');
    overflow-wrap: break-word;
    -webkit-box-orient: vertical;

    @include legacy-typography.base-300-300-regular;
  }

  .more {
    position: absolute;
    right: 0;
    bottom: 0;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-color: var(--alias-color-text-subtler);
    text-decoration-skip-ink: none;
    background-color: var(--alias-color-background-default);

    &::before {
      position: absolute;
      top: 0;
      left: -8px;
      width: 8px;
      height: 100%;
      content: '';
      background-color: var(--alias-color-background-default);
    }

    @include legacy-typography.base-300-300-regular;
  }

  .hidden {
    overflow: hidden;
  }
</style>
